
/**
 * Version check utility for the PWA
 * Checks if the application has a new version available
 */
import { getVersionInfo, setVersionInfo, type VersionInfo } from './version-info';
import { forceAppRefresh } from './app-refresh';
import { isPushActivitySuspended } from './push-activity';

let lastVersionCheckTime = 0;
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const STALENESS_THRESHOLD = 24 * 60 * 60 * 1000; // 24 hours

// Check for application updates by comparing version.json timestamps
export const initVersionCheck = async (): Promise<void> => {
  try {
    // Only check for updates if the page is visible and enough time has passed since last check
    if (document.visibilityState !== 'visible') {
      return;
    }

    // If a push subscription / test is mid-flight on iOS standalone, defer.
    // Forcing a reload right now would tear down the service worker that the
    // push pipeline is depending on.
    if (isPushActivitySuspended()) {
      console.log('[Version] Skipping check — push activity in flight');
      return;
    }

    const now = Date.now();
    if (now - lastVersionCheckTime < VERSION_CHECK_INTERVAL) {
      console.log('[Version] Skipping check - too soon since last check');
      return;
    }
    
    lastVersionCheckTime = now;
    console.log('[Version] Checking for updates...');

    // Clear cache to ensure we get the latest version info
    const fetchOptions = {
      cache: 'no-store' as RequestCache,
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    };

    // Add timestamp to bust any caches
    const response = await fetch(`/version.json?t=${now}`, fetchOptions);
    if (!response.ok) {
      console.error('[Version] Failed to fetch version info:', response.status, response.statusText);
      throw new Error(`Failed to fetch version info: ${response.status} ${response.statusText}`);
    }

    const versionInfo = await response.json();
    
    // Validate version info format
    if (!versionInfo || typeof versionInfo !== 'object' || !versionInfo.buildTimestamp) {
      console.error('[Version] Invalid version info format:', versionInfo);
      throw new Error('Invalid version info format');
    }
    
    // Log version information for debugging
    console.log('[Version] Loaded info:', versionInfo);
    
    // Set global version info
    setVersionInfo(versionInfo as VersionInfo);
    
    const lastCheckedVersion = localStorage.getItem('app_version');
    const lastCheckedVersionNumber = localStorage.getItem('app_version_number');
    
    // If this is the first check or there's no stored version, store current version and exit
    if (!lastCheckedVersion) {
      localStorage.setItem('app_version', versionInfo.buildTimestamp);
      localStorage.setItem('app_version_number', versionInfo.version || '1.0.0');
      console.log('[Version] First check, saving version:', versionInfo.buildTimestamp);
      return;
    }
    
    // Check if version is different from last time (either timestamp or version number)
    const timestampChanged = lastCheckedVersion !== versionInfo.buildTimestamp;
    const versionChanged = lastCheckedVersionNumber !== versionInfo.version;
    
    if (timestampChanged || versionChanged) {
      console.log(`[Version] New version detected! Previous: ${lastCheckedVersion} (${lastCheckedVersionNumber}), Current: ${versionInfo.buildTimestamp} (${versionInfo.version})`);
      
      // Update stored version
      localStorage.setItem('app_version', versionInfo.buildTimestamp);
      localStorage.setItem('app_version_number', versionInfo.version || '1.0.0');
      
      // Check staleness: if the cached version is >24h old, force immediate refresh
      const cachedTimestamp = parseInt(lastCheckedVersion, 10);
      const serverTimestamp = parseInt(versionInfo.buildTimestamp, 10);
      const isStale = !isNaN(cachedTimestamp) && !isNaN(serverTimestamp) && 
                      (serverTimestamp - cachedTimestamp) > STALENESS_THRESHOLD;
      
      if (isStale) {
        console.log('[Version] Build is critically stale (>24h). Forcing immediate refresh.');
        // Send CLEAR_CACHES to service worker if available
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHES' });
        }
        forceAppRefresh();
        return;
      }
      
      // Send CLEAR_CACHES to service worker if available
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_CACHES'
        });
      }
      
      // Auto-refresh silently — the SW toast in main.tsx is the single notification source
      forceAppRefresh();
    } else {
      console.log('[Version] No updates available');
    }
  } catch (error) {
    console.error('[Version] Check failed:', error);
  }
};

// Add global types for the toast function
declare global {
  interface Window {
    showToast: (options: {
      title: string;
      description?: string;
      action?: {
        label: string;
        onClick: () => void;
      }
    }) => void;
  }
}
